import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

// Importaciones de entornos.
import { environment } from '../../environments/environment';

// Importaciones de interfaces.
import { IActivation } from '../interfaces/activation.interface';

/**
 * Servicio principal de la aplicación Dansap.
 *
 * Este servicio actúa como núcleo del sistema, centralizando funcionalidades clave
 * o integrando distintos módulos y servicios de la aplicación.
 *
 * Está disponible de forma global en toda la aplicación gracias al decorador `@Injectable`
 * con `providedIn: 'root'`, lo que garantiza una única instancia (singleton).
 *
 * Puede ser utilizado para inicializar procesos, manejar el estado global, orquestar
 * flujos de datos o actuar como fachada de servicios internos.
 */
@Injectable({
  providedIn: 'root',
})
export class DansapService {
  /**
   * Constructor de la clase ActivationService.
   *
   * Inyecta el servicio `HttpClient`. El servicio `HttpClient` se
   * utiliza para realizar solicitudes HTTP al servidor de autenticación.
   *
   * @param http - Servicio de Angular para realizar solicitudes HTTP.
   */
  constructor(private http: HttpClient) {}

  /**
   * Obtiene una lista de clientes según el término de búsqueda proporcionado.
   *
   * Esta función realiza una solicitud HTTP a la API de Dansap para obtener
   * una lista de clientes que coincidan con el término de búsqueda especificado.
   *
   * @param searchTerm - El término de búsqueda utilizado para filtrar los clientes.
   *
   * @returns Una promesa que resuelve con la lista de clientes que coinciden con el término de búsqueda.
   */
  getClientsBySearchTerm(searchTerm: string): Promise<any> {
    return firstValueFrom(
      this.http.get(`${environment.dansapApiUrl}/client`, {
        params: {
          key: environment.dansapApiKey,
          searchTerm,
        },
      })
    );
  }

  /**
   * Obtiene un cliente específico según su código único.
   *
   * Esta función realiza una solicitud HTTP a la API de Dansap para obtener
   * los datos de un cliente utilizando su código único como criterio de búsqueda.
   *
   * @param code - El código único del cliente que se desea obtener.
   *
   * @returns Una promesa que resuelve con los datos del cliente correspondiente.
   */
  getClientByCode(code: number): Promise<any> {
    return firstValueFrom(
      this.http.get(`${environment.dansapApiUrl}/client/code`, {
        params: {
          key: environment.dansapApiKey,
          codigo: code,
        },
      })
    );
  }

  /**
   * Envía una solicitud de activación a la API de Dansap.
   *
   * Esta función realiza una solicitud HTTP POST a la API de Dansap para enviar
   * los datos de activación proporcionados. Los datos incluyen información del empleado,
   * cliente, licencia y detalles de la activación.
   *
   * @param activation - Objeto de activación que contiene los datos necesarios para la solicitud.
   *
   * @returns Una promesa que resuelve con la respuesta de la API tras procesar la solicitud.
   */
  sendActivation(activation: IActivation): Promise<any> {
    return firstValueFrom(
      this.http.post(environment.dansapApiUrl, {
        key: environment.dansapApiKey,
        employeeDisplayName: activation.employee.displayName,
        licenseHost: activation.host.type,
        customerCode: activation.customer.reference,
        customerNif: activation.customer.nif,
        customerName: activation.customer.name,
        customerAddress: activation.customer.address,
        customerTelephone: activation.customer.telephone,
        customerCity: activation.customer.city,
        customerProvince: activation.customer.province,
        customerPostalCode: activation.customer.postalCode,
        customerCountry: activation.customer.country,
        activationSerial: activation.serial,
        activationExpirationType: activation.expirationType,
        activationExpirationDays: activation.expirationDays,
        activationComment: activation.comment,
      })
    );
  }
}
